import { CELL_VALUE_OPERATOR_ALIASES, CELL_VALUE_OPERATORS, CHART_SERIES_BY, PRESET_CRITERIA, resolveEnum, TOP_BOTTOM_TYPES, VALIDATION_ALERT_STYLES, VALIDATION_OPERATOR_ALIASES, VALIDATION_OPERATORS, } from './excelEnums';
function columnNumberToLetter(col) {
    let result = '';
    let n = col;
    while (n > 0) {
        n--;
        result = String.fromCharCode((n % 26) + 65) + result;
        n = Math.floor(n / 26);
    }
    return result;
}
function columnLetterToNumber(col) {
    const upper = col.toUpperCase();
    if (!/^[A-Z]+$/.test(upper)) {
        throw new Error(`Invalid column letter: "${col}"`);
    }
    let result = 0;
    for (let i = 0; i < upper.length; i++) {
        result = result * 26 + (upper.charCodeAt(i) - 64);
    }
    return result;
}
async function requireTable(context, name) {
    const table = context.workbook.tables.getItemOrNullObject(name);
    table.load('isNullObject');
    await context.sync();
    if (table.isNullObject) {
        throw new Error(`Table "${name}" not found`);
    }
    return table;
}
function getWorksheet(context, name) {
    const ws = context.workbook.worksheets.getItemOrNullObject(name);
    ws.load('isNullObject');
    return ws;
}
async function requireWorksheet(context, name) {
    const ws = getWorksheet(context, name);
    await context.sync();
    if (ws.isNullObject) {
        throw new Error(`Worksheet "${name}" not found`);
    }
    return ws;
}
export const excelTools = [
    // ==================== READ TOOLS ====================
    {
        tool: {
            name: 'excel_get_workbook_data',
            description: 'Get all data from all sheets in the current workbook',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const text = await Excel.run(async (context) => {
                const worksheets = context.workbook.worksheets;
                worksheets.load('items/name');
                await context.sync();
                const sheetContents = [];
                for (const worksheet of worksheets.items) {
                    const usedRange = worksheet.getUsedRangeOrNullObject();
                    usedRange.load('values');
                    await context.sync();
                    if (!usedRange.isNullObject && usedRange.values) {
                        const rows = usedRange.values.map((row) => row
                            .map((cell) => cell !== null && cell !== undefined ? String(cell) : '')
                            .join('\t'));
                        sheetContents.push(`=== Sheet: ${worksheet.name} ===\n${rows.join('\n')}`);
                    }
                    else {
                        sheetContents.push(`=== Sheet: ${worksheet.name} ===\n(empty)`);
                    }
                }
                return sheetContents.join('\n\n');
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_sheet_data',
            description: 'Get data from a specific sheet by name',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                },
                required: ['sheet_name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const text = await Excel.run(async (context) => {
                const worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
                worksheet.load('name');
                await context.sync();
                if (worksheet.isNullObject) {
                    return `Sheet "${sheetName}" not found`;
                }
                const usedRange = worksheet.getUsedRangeOrNullObject();
                usedRange.load('values');
                await context.sync();
                if (!usedRange.isNullObject && usedRange.values) {
                    const rows = usedRange.values.map((row) => row
                        .map((cell) => cell !== null && cell !== undefined ? String(cell) : '')
                        .join('\t'));
                    return rows.join('\n');
                }
                return '(empty)';
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_sheet_names',
            description: 'List all sheet names in the workbook',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const names = await Excel.run(async (context) => {
                const worksheets = context.workbook.worksheets;
                worksheets.load('items/name');
                await context.sync();
                return worksheets.items.map((ws) => ws.name);
            });
            return { content: [{ type: 'text', text: JSON.stringify(names) }] };
        },
    },
    {
        tool: {
            name: 'excel_get_range_values',
            description: 'Get values from a range on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:C10")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const text = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                range.load('values');
                await context.sync();
                const rows = range.values.map((row) => row
                    .map((cell) => (cell !== null && cell !== undefined ? String(cell) : ''))
                    .join('\t'));
                return rows.join('\n');
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_range_formulas',
            description: 'Get formulas from a range on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:C10")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const text = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                range.load('formulas');
                await context.sync();
                const rows = range.formulas.map((row) => row
                    .map((cell) => (cell !== null && cell !== undefined ? String(cell) : ''))
                    .join('\t'));
                return rows.join('\n');
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_selection',
            description: 'Get the currently selected range including its address, values, and formulas',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const text = await Excel.run(async (context) => {
                const range = context.workbook.getSelectedRange();
                range.load(['address', 'values', 'formulas']);
                await context.sync();
                const values = range.values.map((row) => row
                    .map((cell) => (cell !== null && cell !== undefined ? String(cell) : ''))
                    .join('\t'));
                const formulas = range.formulas.map((row) => row
                    .map((cell) => (cell !== null && cell !== undefined ? String(cell) : ''))
                    .join('\t'));
                return JSON.stringify({
                    address: range.address,
                    values: values.join('\n'),
                    formulas: formulas.join('\n'),
                });
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_table_data',
            description: 'Get data from a named table including headers and body rows. If no table_name is provided, lists all tables in the workbook.',
            inputSchema: {
                type: 'object',
                properties: {
                    table_name: {
                        type: 'string',
                        description: 'The name of the table. If omitted, returns a list of all tables.',
                    },
                },
            },
        },
        execute: async (args) => {
            const tableName = args.table_name;
            const text = await Excel.run(async (context) => {
                if (!tableName) {
                    const tables = context.workbook.tables;
                    tables.load(['items/name', 'items/id']);
                    await context.sync();
                    const tableList = tables.items.map((t) => ({
                        name: t.name,
                        id: t.id,
                    }));
                    return JSON.stringify(tableList);
                }
                const table = await requireTable(context, tableName);
                const tableRows = table.rows;
                tableRows.load('count');
                const headerRange = table.getHeaderRowRange();
                headerRange.load('values');
                await context.sync();
                const headers = headerRange.values[0].map(String);
                if (tableRows.count === 0) {
                    return JSON.stringify({ headers, rows: [] });
                }
                const bodyRange = table.getDataBodyRange();
                bodyRange.load('values');
                await context.sync();
                const rows = bodyRange.values.map((row) => row.map((cell) => (cell !== null && cell !== undefined ? String(cell) : '')));
                return JSON.stringify({ headers, rows });
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_get_workbook_properties',
            description: 'Get workbook document properties (title, author, subject, keywords, etc.)',
            inputSchema: {
                type: 'object',
                properties: {},
            },
        },
        execute: async () => {
            const text = await Excel.run(async (context) => {
                const props = context.workbook.properties;
                props.load([
                    'title',
                    'author',
                    'subject',
                    'keywords',
                    'comments',
                    'category',
                    'manager',
                    'company',
                    'creationDate',
                    'lastAuthor',
                    'revisionNumber',
                ]);
                await context.sync();
                return JSON.stringify({
                    title: props.title,
                    author: props.author,
                    subject: props.subject,
                    keywords: props.keywords,
                    comments: props.comments,
                    category: props.category,
                    manager: props.manager,
                    company: props.company,
                    creationDate: props.creationDate,
                    lastAuthor: props.lastAuthor,
                    revisionNumber: props.revisionNumber,
                });
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    // ==================== WRITE / EDIT TOOLS ====================
    {
        tool: {
            name: 'excel_set_cell_value',
            description: 'Set a value at a specific cell address on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    address: {
                        type: 'string',
                        description: 'The cell address (e.g. "A1")',
                    },
                    value: {
                        type: 'string',
                        description: 'The value to set',
                    },
                },
                required: ['sheet_name', 'address', 'value'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const address = args.address;
            const value = args.value;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(address);
                range.values = [[value]];
                await context.sync();
            });
            return {
                content: [{ type: 'text', text: `Set ${address} on "${sheetName}" to "${value}"` }],
            };
        },
    },
    {
        tool: {
            name: 'excel_set_range_values',
            description: 'Set values for a rectangular range on a sheet. Values are provided as a 2D array of strings.',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    start_address: {
                        type: 'string',
                        description: 'The top-left cell address (e.g. "A1")',
                    },
                    values: {
                        type: 'array',
                        items: {
                            type: 'array',
                            items: { type: 'string' },
                        },
                        description: 'A 2D array of values (rows x columns)',
                    },
                },
                required: ['sheet_name', 'start_address', 'values'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const startAddress = args.start_address;
            const values = args.values;
            if (!values.length || !values[0].length) {
                return {
                    content: [{ type: 'text', text: 'Error: values array must not be empty' }],
                };
            }
            const rowCount = values.length;
            const colCount = values[0].length;
            for (let i = 1; i < rowCount; i++) {
                if (values[i].length !== colCount) {
                    throw new Error(`Jagged array: row 0 has ${colCount} columns but row ${i} has ${values[i].length}`);
                }
            }
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const startRange = worksheet.getRange(startAddress);
                const targetRange = startRange.getResizedRange(rowCount - 1, colCount - 1);
                targetRange.values = values;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set ${rowCount}x${colCount} range starting at ${startAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_set_cell_formula',
            description: 'Set a formula at a specific cell address on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    address: {
                        type: 'string',
                        description: 'The cell address (e.g. "A1")',
                    },
                    formula: {
                        type: 'string',
                        description: 'The formula to set (e.g. "=SUM(A1:A10)")',
                    },
                },
                required: ['sheet_name', 'address', 'formula'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const address = args.address;
            const formula = args.formula;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(address);
                range.formulas = [[formula]];
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set formula ${formula} at ${address} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_clear_range',
            description: 'Clear contents, formats, or all from a range',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:C10")',
                    },
                    type: {
                        type: 'string',
                        enum: ['all', 'contents', 'formats', 'hyperlinks'],
                        description: 'What to clear (default: "all")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const clearType = args.type ?? 'all';
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                switch (clearType) {
                    case 'contents':
                        range.clear(Excel.ClearApplyTo.contents);
                        break;
                    case 'formats':
                        range.clear(Excel.ClearApplyTo.formats);
                        break;
                    case 'hyperlinks':
                        range.clear(Excel.ClearApplyTo.hyperlinks);
                        break;
                    default:
                        range.clear(Excel.ClearApplyTo.all);
                        break;
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Cleared ${clearType} from ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== WORKSHEET MANAGEMENT ====================
    {
        tool: {
            name: 'excel_add_worksheet',
            description: 'Add a new worksheet to the workbook',
            inputSchema: {
                type: 'object',
                properties: {
                    name: {
                        type: 'string',
                        description: 'Name for the new worksheet (optional, auto-generated if omitted)',
                    },
                },
            },
        },
        execute: async (args) => {
            const name = args.name;
            const createdName = await Excel.run(async (context) => {
                const worksheet = context.workbook.worksheets.add(name);
                worksheet.load('name');
                await context.sync();
                return worksheet.name;
            });
            return {
                content: [{ type: 'text', text: `Created worksheet "${createdName}"` }],
            };
        },
    },
    {
        tool: {
            name: 'excel_delete_worksheet',
            description: 'Delete a worksheet by name',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet to delete',
                    },
                },
                required: ['sheet_name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            await Excel.run(async (context) => {
                const worksheets = context.workbook.worksheets;
                worksheets.load('items');
                await context.sync();
                if (worksheets.items.length <= 1) {
                    throw new Error('Cannot delete the last worksheet in the workbook');
                }
                const worksheet = await requireWorksheet(context, sheetName);
                worksheet.delete();
                await context.sync();
            });
            return {
                content: [{ type: 'text', text: `Deleted worksheet "${sheetName}"` }],
            };
        },
    },
    {
        tool: {
            name: 'excel_rename_worksheet',
            description: 'Rename a worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The current name of the worksheet',
                    },
                    new_name: {
                        type: 'string',
                        description: 'The new name for the worksheet',
                    },
                },
                required: ['sheet_name', 'new_name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const newName = args.new_name;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                worksheet.name = newName;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Renamed worksheet "${sheetName}" to "${newName}"`,
                    },
                ],
            };
        },
    },
    // ==================== ROW / COLUMN OPERATIONS ====================
    {
        tool: {
            name: 'excel_insert_rows',
            description: 'Insert empty rows at a specified position on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    row: {
                        type: 'number',
                        description: 'The 1-based row number where rows will be inserted',
                    },
                    count: {
                        type: 'number',
                        description: 'Number of rows to insert (default: 1)',
                    },
                },
                required: ['sheet_name', 'row'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const row = args.row;
            const count = args.count ?? 1;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const rangeAddress = `${row}:${row + count - 1}`;
                const range = worksheet.getRange(rangeAddress);
                range.insert(Excel.InsertShiftDirection.down);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted ${count} row(s) at row ${row} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_delete_rows',
            description: 'Delete rows at a specified position on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    row: {
                        type: 'number',
                        description: 'The 1-based row number to start deleting from',
                    },
                    count: {
                        type: 'number',
                        description: 'Number of rows to delete (default: 1)',
                    },
                },
                required: ['sheet_name', 'row'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const row = args.row;
            const count = args.count ?? 1;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const rangeAddress = `${row}:${row + count - 1}`;
                const range = worksheet.getRange(rangeAddress);
                range.delete(Excel.DeleteShiftDirection.up);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Deleted ${count} row(s) starting at row ${row} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_insert_columns',
            description: 'Insert empty columns at a specified position on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    column: {
                        type: 'string',
                        description: 'The column letter where columns will be inserted (e.g. "C")',
                    },
                    count: {
                        type: 'number',
                        description: 'Number of columns to insert (default: 1)',
                    },
                },
                required: ['sheet_name', 'column'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const column = args.column;
            const count = args.count ?? 1;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const startCol = column.toUpperCase();
                const startNum = columnLetterToNumber(startCol);
                const endCol = columnNumberToLetter(startNum + count - 1);
                const rangeAddress = `${startCol}:${endCol}`;
                const range = worksheet.getRange(rangeAddress);
                range.insert(Excel.InsertShiftDirection.right);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted ${count} column(s) at column ${column} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_delete_columns',
            description: 'Delete columns at a specified position on a sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    column: {
                        type: 'string',
                        description: 'The column letter to start deleting from (e.g. "C")',
                    },
                    count: {
                        type: 'number',
                        description: 'Number of columns to delete (default: 1)',
                    },
                },
                required: ['sheet_name', 'column'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const column = args.column;
            const count = args.count ?? 1;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const startCol = column.toUpperCase();
                const startNum = columnLetterToNumber(startCol);
                const endCol = columnNumberToLetter(startNum + count - 1);
                const rangeAddress = `${startCol}:${endCol}`;
                const range = worksheet.getRange(rangeAddress);
                range.delete(Excel.DeleteShiftDirection.left);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Deleted ${count} column(s) starting at column ${column} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== FORMATTING ====================
    {
        tool: {
            name: 'excel_format_range',
            description: 'Apply formatting to a range. Supports font (bold, italic, underline, color, size, name), fill color, number format, horizontal/vertical alignment, wrap text, and borders.',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:C10")',
                    },
                    bold: { type: 'boolean', description: 'Set font bold' },
                    italic: { type: 'boolean', description: 'Set font italic' },
                    underline: {
                        type: 'string',
                        enum: ['None', 'Single', 'Double'],
                        description: 'Set underline style',
                    },
                    font_color: {
                        type: 'string',
                        description: 'Font color as hex (e.g. "#FF0000")',
                    },
                    font_size: { type: 'number', description: 'Font size in points' },
                    font_name: {
                        type: 'string',
                        description: 'Font name (e.g. "Calibri", "Arial")',
                    },
                    fill_color: {
                        type: 'string',
                        description: 'Fill/background color as hex (e.g. "#FFFF00")',
                    },
                    number_format: {
                        type: 'string',
                        description: 'Number format string (e.g. "#,##0.00", "0%", "yyyy-mm-dd", "$#,##0.00")',
                    },
                    horizontal_alignment: {
                        type: 'string',
                        enum: [
                            'General',
                            'Left',
                            'Center',
                            'Right',
                            'Fill',
                            'Justify',
                            'Distributed',
                        ],
                        description: 'Horizontal alignment',
                    },
                    vertical_alignment: {
                        type: 'string',
                        enum: ['Top', 'Center', 'Bottom', 'Justify', 'Distributed'],
                        description: 'Vertical alignment',
                    },
                    wrap_text: { type: 'boolean', description: 'Enable text wrapping' },
                    borders: {
                        type: 'string',
                        enum: ['none', 'thin', 'medium', 'thick'],
                        description: 'Apply border style to all edges of the range',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                if (args.bold !== undefined)
                    range.format.font.bold = args.bold;
                if (args.italic !== undefined)
                    range.format.font.italic = args.italic;
                if (args.underline !== undefined)
                    range.format.font.underline = args.underline;
                if (args.font_color !== undefined)
                    range.format.font.color = args.font_color;
                if (args.font_size !== undefined)
                    range.format.font.size = args.font_size;
                if (args.font_name !== undefined)
                    range.format.font.name = args.font_name;
                if (args.fill_color !== undefined)
                    range.format.fill.color = args.fill_color;
                if (args.number_format !== undefined)
                    range.numberFormat = [[args.number_format]];
                if (args.horizontal_alignment !== undefined)
                    range.format.horizontalAlignment =
                        args.horizontal_alignment;
                if (args.vertical_alignment !== undefined)
                    range.format.verticalAlignment =
                        args.vertical_alignment;
                if (args.wrap_text !== undefined)
                    range.format.wrapText = args.wrap_text;
                if (args.borders !== undefined) {
                    const borderIndices = [
                        Excel.BorderIndex.edgeTop,
                        Excel.BorderIndex.edgeBottom,
                        Excel.BorderIndex.edgeLeft,
                        Excel.BorderIndex.edgeRight,
                        Excel.BorderIndex.insideHorizontal,
                        Excel.BorderIndex.insideVertical,
                    ];
                    if (args.borders === 'none') {
                        for (const index of borderIndices) {
                            range.format.borders.getItem(index).style = Excel.BorderLineStyle.none;
                        }
                    }
                    else {
                        const weight = args.borders === 'thick'
                            ? Excel.BorderWeight.thick
                            : args.borders === 'medium'
                                ? Excel.BorderWeight.medium
                                : Excel.BorderWeight.thin;
                        for (const index of borderIndices) {
                            const border = range.format.borders.getItem(index);
                            border.style = Excel.BorderLineStyle.continuous;
                            border.weight = weight;
                        }
                    }
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Applied formatting to ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_set_number_format',
            description: 'Set the number format for a range (e.g. currency, percentage, date)',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "B2:B100")',
                    },
                    format: {
                        type: 'string',
                        description: 'The number format string (e.g. "$#,##0.00", "0.00%", "yyyy-mm-dd", "#,##0")',
                    },
                },
                required: ['sheet_name', 'range', 'format'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const format = args.format;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                range.numberFormat = [[format]];
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set number format "${format}" on ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_autofit_columns',
            description: 'Auto-fit column widths for a range to fit their contents',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A:D" for columns A-D, or "A1:D100")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                range.format.autofitColumns();
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Auto-fitted columns for ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_autofit_rows',
            description: 'Auto-fit row heights for a range to fit their contents',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "1:10" or "A1:D100")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                range.format.autofitRows();
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Auto-fitted rows for ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_merge_cells',
            description: 'Merge or unmerge cells in a range',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:C1")',
                    },
                    merge: {
                        type: 'boolean',
                        description: 'true to merge, false to unmerge',
                    },
                    across: {
                        type: 'boolean',
                        description: 'If true, merge cells in each row separately (default: false)',
                    },
                },
                required: ['sheet_name', 'range', 'merge'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const merge = args.merge;
            const across = args.across ?? false;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                if (merge) {
                    range.merge(across);
                }
                else {
                    range.unmerge();
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `${merge ? 'Merged' : 'Unmerged'} ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_add_conditional_format',
            description: 'Add conditional formatting to a range. Supports cell value rules, color scales, data bars, icon sets, and top/bottom rules.',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "A1:A100")',
                    },
                    type: {
                        type: 'string',
                        enum: [
                            'cellValue',
                            'colorScale',
                            'dataBar',
                            'iconSet',
                            'topBottom',
                            'presetCriteria',
                        ],
                        description: 'The type of conditional format',
                    },
                    rule: {
                        type: 'object',
                        description: 'Rule configuration. For cellValue: {operator: "between"|"notBetween"|"equalTo"|"notEqualTo"|"greaterThan"|"lessThan"|"greaterThanOrEqual"|"lessThanOrEqual" (default "greaterThan"), formula1, formula2?, format: {font_color?, fill_color?}}. For topBottom: {rank, type: "topItems"|"bottomItems"|"topPercent"|"bottomPercent"}. For presetCriteria: {criterion: ' +
                            PRESET_CRITERIA.map((c) => `"${c}"`).join('|') +
                            ' (default "blanks"), format: {font_color?, fill_color?}}.',
                    },
                },
                required: ['sheet_name', 'range', 'type'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const formatType = args.type;
            const rule = args.rule ?? {};
            // Resolve enum-valued rule fields before touching the workbook so a bad value fails
            // without adding a half-configured conditional format.
            const cellValueOperator = formatType === 'cellValue'
                ? resolveEnum('operator', rule.operator, 'greaterThan', Excel.ConditionalCellValueOperator, CELL_VALUE_OPERATORS, CELL_VALUE_OPERATOR_ALIASES)
                : undefined;
            const topBottomType = formatType === 'topBottom'
                ? resolveEnum('type', rule.type, 'topItems', Excel.ConditionalTopBottomCriterionType, TOP_BOTTOM_TYPES)
                : undefined;
            const presetCriterion = formatType === 'presetCriteria'
                ? resolveEnum('criterion', rule.criterion, 'blanks', Excel.ConditionalFormatPresetCriterion, PRESET_CRITERIA)
                : undefined;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                const conditionalFormats = range.conditionalFormats;
                switch (formatType) {
                    case 'cellValue': {
                        const cf = conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
                        const ruleConfig = rule;
                        cf.cellValue.rule = {
                            operator: cellValueOperator,
                            formula1: ruleConfig.formula1 ?? '0',
                            formula2: ruleConfig.formula2,
                        };
                        if (ruleConfig.format?.font_color)
                            cf.cellValue.format.font.color = ruleConfig.format.font_color;
                        if (ruleConfig.format?.fill_color)
                            cf.cellValue.format.fill.color = ruleConfig.format.fill_color;
                        break;
                    }
                    case 'colorScale': {
                        conditionalFormats.add(Excel.ConditionalFormatType.colorScale);
                        break;
                    }
                    case 'dataBar': {
                        conditionalFormats.add(Excel.ConditionalFormatType.dataBar);
                        break;
                    }
                    case 'iconSet': {
                        conditionalFormats.add(Excel.ConditionalFormatType.iconSet);
                        break;
                    }
                    case 'topBottom': {
                        const cf = conditionalFormats.add(Excel.ConditionalFormatType.topBottom);
                        const topRule = rule;
                        cf.topBottom.rule = {
                            rank: topRule.rank ?? 10,
                            type: topBottomType,
                        };
                        break;
                    }
                    case 'presetCriteria': {
                        const cf = conditionalFormats.add(Excel.ConditionalFormatType.presetCriteria);
                        const presetRule = rule;
                        cf.preset.rule = {
                            criterion: presetCriterion,
                        };
                        if (presetRule.format?.font_color)
                            cf.preset.format.font.color = presetRule.format.font_color;
                        if (presetRule.format?.fill_color)
                            cf.preset.format.fill.color = presetRule.format.fill_color;
                        break;
                    }
                    default:
                        throw new Error(`Unknown conditional format type: "${formatType}". Valid types: cellValue, colorScale, dataBar, iconSet, topBottom, presetCriteria`);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Added ${formatType} conditional format to ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== SORTING & FILTERING ====================
    {
        tool: {
            name: 'excel_sort_range',
            description: 'Sort a range by one or more columns',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address to sort (e.g. "A1:D100")',
                    },
                    sort_fields: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                column_index: {
                                    type: 'number',
                                    description: '0-based column index within the range',
                                },
                                ascending: {
                                    type: 'boolean',
                                    description: 'Sort ascending (default: true)',
                                },
                            },
                            required: ['column_index'],
                        },
                        description: 'Array of sort field definitions',
                    },
                    has_headers: {
                        type: 'boolean',
                        description: 'Whether the range has a header row (default: true)',
                    },
                },
                required: ['sheet_name', 'range', 'sort_fields'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const sortFields = args.sort_fields;
            const hasHeaders = args.has_headers ?? true;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                const sort = range.sort;
                const fields = sortFields.map((f) => ({
                    key: f.column_index,
                    sortOn: Excel.SortOn.value,
                    ascending: f.ascending ?? true,
                }));
                sort.apply(fields, hasHeaders);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Sorted ${rangeAddress} on "${sheetName}" by ${sortFields.length} field(s)`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_find_replace',
            description: 'Find and replace text within a range or entire sheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address to search in. If omitted, searches the used range.',
                    },
                    find: {
                        type: 'string',
                        description: 'The text to find',
                    },
                    replace: {
                        type: 'string',
                        description: 'The text to replace with. If omitted, returns the count of matches found.',
                    },
                },
                required: ['sheet_name', 'find'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const findText = args.find;
            const replaceText = args.replace;
            const text = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = rangeAddress
                    ? worksheet.getRange(rangeAddress)
                    : worksheet.getUsedRangeOrNullObject();
                if (!rangeAddress) {
                    range.load('isNullObject');
                    await context.sync();
                    if (range.isNullObject) {
                        return `Sheet "${sheetName}" is empty`;
                    }
                }
                if (replaceText !== undefined) {
                    const count = range.replaceAll(findText, replaceText, {
                        completeMatch: false,
                        matchCase: false,
                    });
                    await context.sync();
                    return `Replaced ${count.value} occurrence(s) of "${findText}" with "${replaceText}"`;
                }
                else {
                    const found = range.findOrNullObject(findText, {
                        completeMatch: false,
                        matchCase: false,
                    });
                    found.load('address');
                    await context.sync();
                    if (found.isNullObject) {
                        return `"${findText}" not found`;
                    }
                    return `Found "${findText}" at ${found.address}`;
                }
            });
            return { content: [{ type: 'text', text }] };
        },
    },
    {
        tool: {
            name: 'excel_filter_table',
            description: 'Apply or clear a filter on a table column',
            inputSchema: {
                type: 'object',
                properties: {
                    table_name: {
                        type: 'string',
                        description: 'The name of the table',
                    },
                    column_index: {
                        type: 'number',
                        description: '0-based column index to filter',
                    },
                    values: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Values to filter by (show only rows matching these values). If omitted, clears the filter on this column.',
                    },
                },
                required: ['table_name', 'column_index'],
            },
        },
        execute: async (args) => {
            const tableName = args.table_name;
            const columnIndex = args.column_index;
            const values = args.values;
            await Excel.run(async (context) => {
                const table = await requireTable(context, tableName);
                const column = table.columns.getItemAt(columnIndex);
                column.load('name');
                await context.sync();
                if (values && values.length > 0) {
                    column.filter.applyValuesFilter(values);
                }
                else {
                    column.filter.clear();
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: values
                            ? `Filtered table "${tableName}" column ${columnIndex} to values: ${values.join(', ')}`
                            : `Cleared filter on table "${tableName}" column ${columnIndex}`,
                    },
                ],
            };
        },
    },
    // ==================== TABLE OPERATIONS ====================
    {
        tool: {
            name: 'excel_create_table',
            description: 'Create a table from a range. Tables enable sorting, filtering, and structured references.',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address for the table (e.g. "A1:D10")',
                    },
                    has_headers: {
                        type: 'boolean',
                        description: 'Whether the first row contains headers (default: true)',
                    },
                    name: {
                        type: 'string',
                        description: 'Optional name for the table',
                    },
                    style: {
                        type: 'string',
                        description: 'Optional table style name (e.g. "TableStyleMedium2", "TableStyleLight1")',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const hasHeaders = args.has_headers ?? true;
            const name = args.name;
            const style = args.style;
            const tableName = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const table = worksheet.tables.add(rangeAddress, hasHeaders);
                if (name)
                    table.name = name;
                if (style)
                    table.style = style;
                table.load('name');
                await context.sync();
                return table.name;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Created table "${tableName}" from ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_add_table_row',
            description: 'Add a row to an existing table',
            inputSchema: {
                type: 'object',
                properties: {
                    table_name: {
                        type: 'string',
                        description: 'The name of the table',
                    },
                    values: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of values for the new row',
                    },
                    index: {
                        type: 'number',
                        description: '0-based index to insert at. If omitted, appends to the end.',
                    },
                },
                required: ['table_name', 'values'],
            },
        },
        execute: async (args) => {
            const tableName = args.table_name;
            const values = args.values;
            const index = args.index;
            await Excel.run(async (context) => {
                const table = await requireTable(context, tableName);
                table.rows.add(index, [values]);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Added row to table "${tableName}"${index !== undefined ? ` at index ${index}` : ''}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_delete_table_row',
            description: 'Delete a row from an existing table by index',
            inputSchema: {
                type: 'object',
                properties: {
                    table_name: {
                        type: 'string',
                        description: 'The name of the table',
                    },
                    index: {
                        type: 'number',
                        description: '0-based index of the row to delete',
                    },
                },
                required: ['table_name', 'index'],
            },
        },
        execute: async (args) => {
            const tableName = args.table_name;
            const index = args.index;
            await Excel.run(async (context) => {
                const table = await requireTable(context, tableName);
                const row = table.rows.getItemAt(index);
                row.delete();
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Deleted row ${index} from table "${tableName}"`,
                    },
                ],
            };
        },
    },
    // ==================== CHARTS ====================
    {
        tool: {
            name: 'excel_add_chart',
            description: 'Create a chart from a data range',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet containing the data',
                    },
                    range: {
                        type: 'string',
                        description: 'The data range for the chart (e.g. "A1:D10")',
                    },
                    chart_type: {
                        type: 'string',
                        enum: [
                            'ColumnClustered',
                            'ColumnStacked',
                            'ColumnStacked100',
                            'BarClustered',
                            'BarStacked',
                            'BarStacked100',
                            'Line',
                            'LineMarkers',
                            'Pie',
                            'Doughnut',
                            'Area',
                            'AreaStacked',
                            'XYScatter',
                            'XYScatterLines',
                            'Radar',
                            'Treemap',
                            'Sunburst',
                            'Waterfall',
                            'Funnel',
                        ],
                        description: 'The chart type (default: "ColumnClustered")',
                    },
                    title: {
                        type: 'string',
                        description: 'Optional chart title',
                    },
                    series_by: {
                        type: 'string',
                        enum: ['auto', 'columns', 'rows'],
                        description: 'How to interpret the data series (default: "auto")',
                    },
                    target_sheet: {
                        type: 'string',
                        description: 'Sheet to place the chart on (default: same as data sheet)',
                    },
                },
                required: ['sheet_name', 'range'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const chartType = args.chart_type ?? 'ColumnClustered';
            const title = args.title;
            const seriesBy = resolveEnum('series_by', args.series_by, 'auto', Excel.ChartSeriesBy, CHART_SERIES_BY);
            const targetSheet = args.target_sheet;
            const chartName = await Excel.run(async (context) => {
                const sourceSheet = await requireWorksheet(context, sheetName);
                const dataRange = sourceSheet.getRange(rangeAddress);
                const destSheet = targetSheet
                    ? await requireWorksheet(context, targetSheet)
                    : sourceSheet;
                const chart = destSheet.charts.add(chartType, dataRange, seriesBy);
                if (title) {
                    chart.title.text = title;
                    chart.title.visible = true;
                }
                chart.load('name');
                await context.sync();
                return chart.name;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Created ${chartType} chart "${chartName}" from ${rangeAddress}${title ? ` with title "${title}"` : ''}`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_get_chart_image',
            description: 'Export a chart as a Base64-encoded PNG image',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet containing the chart',
                    },
                    chart_name: {
                        type: 'string',
                        description: 'The name of the chart',
                    },
                },
                required: ['sheet_name', 'chart_name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const chartName = args.chart_name;
            const imageBase64 = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const chart = worksheet.charts.getItem(chartName);
                const image = chart.getImage();
                await context.sync();
                return image.value;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `data:image/png;base64,${imageBase64}`,
                    },
                ],
            };
        },
    },
    // ==================== DATA VALIDATION ====================
    {
        tool: {
            name: 'excel_set_data_validation',
            description: 'Set data validation rules on a range. Supports dropdown lists, number ranges, date ranges, text length, and custom formulas.',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    range: {
                        type: 'string',
                        description: 'The range address (e.g. "B2:B100")',
                    },
                    type: {
                        type: 'string',
                        enum: ['list', 'wholeNumber', 'decimal', 'date', 'textLength', 'custom'],
                        description: 'The type of validation',
                    },
                    rule: {
                        type: 'object',
                        description: 'Rule configuration. For list: {values: ["A","B","C"]}. For wholeNumber/decimal/date/textLength: {operator: "between"|"notBetween"|"equalTo"|"notEqualTo"|"greaterThan"|"lessThan"|"greaterThanOrEqualTo"|"lessThanOrEqualTo" (default "between"), formula1: "1", formula2: "100"}. For custom: {formula: "=A1>0"}.',
                    },
                    prompt_message: {
                        type: 'string',
                        description: 'Input message shown when cell is selected',
                    },
                    prompt_title: {
                        type: 'string',
                        description: 'Title for the input message',
                    },
                    error_message: {
                        type: 'string',
                        description: 'Error message shown for invalid input',
                    },
                    error_title: {
                        type: 'string',
                        description: 'Title for the error message',
                    },
                    error_style: {
                        type: 'string',
                        enum: ['stop', 'warning', 'information'],
                        description: 'Error alert style (default: "stop")',
                    },
                },
                required: ['sheet_name', 'range', 'type', 'rule'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const validationType = args.type;
            const rule = args.rule;
            // Resolve enum-valued fields before touching the workbook. error_style is only
            // applied when an error message/title is set, so it is only validated then.
            const validationOperator = ['wholeNumber', 'decimal', 'date', 'textLength'].includes(validationType)
                ? resolveEnum('operator', rule.operator, 'between', Excel.DataValidationOperator, VALIDATION_OPERATORS, VALIDATION_OPERATOR_ALIASES)
                : undefined;
            const alertStyle = args.error_message || args.error_title
                ? resolveEnum('error_style', args.error_style, 'stop', Excel.DataValidationAlertStyle, VALIDATION_ALERT_STYLES)
                : undefined;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(rangeAddress);
                const validation = range.dataValidation;
                switch (validationType) {
                    case 'list': {
                        const values = rule.values;
                        if (!values || !values.length) {
                            throw new Error('List validation requires a non-empty "values" array in the rule');
                        }
                        validation.rule = {
                            list: {
                                inCellDropDown: true,
                                source: values.join(','),
                            },
                        };
                        break;
                    }
                    case 'wholeNumber':
                        validation.rule = {
                            wholeNumber: {
                                operator: validationOperator,
                                formula1: String(rule.formula1 ?? '0'),
                                formula2: rule.formula2 !== undefined ? String(rule.formula2) : undefined,
                            },
                        };
                        break;
                    case 'decimal':
                        validation.rule = {
                            decimal: {
                                operator: validationOperator,
                                formula1: String(rule.formula1 ?? '0'),
                                formula2: rule.formula2 !== undefined ? String(rule.formula2) : undefined,
                            },
                        };
                        break;
                    case 'date':
                        validation.rule = {
                            date: {
                                operator: validationOperator,
                                formula1: String(rule.formula1 ?? ''),
                                formula2: rule.formula2 !== undefined ? String(rule.formula2) : undefined,
                            },
                        };
                        break;
                    case 'textLength':
                        validation.rule = {
                            textLength: {
                                operator: validationOperator,
                                formula1: String(rule.formula1 ?? '0'),
                                formula2: rule.formula2 !== undefined ? String(rule.formula2) : undefined,
                            },
                        };
                        break;
                    case 'custom':
                        validation.rule = {
                            custom: {
                                formula: String(rule.formula ?? ''),
                            },
                        };
                        break;
                }
                if (args.prompt_message || args.prompt_title) {
                    validation.prompt = {
                        showPrompt: true,
                        message: args.prompt_message ?? '',
                        title: args.prompt_title ?? '',
                    };
                }
                if (args.error_message || args.error_title) {
                    validation.errorAlert = {
                        showAlert: true,
                        style: alertStyle,
                        message: args.error_message ?? '',
                        title: args.error_title ?? '',
                    };
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set ${validationType} validation on ${rangeAddress} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== PIVOT TABLES ====================
    {
        tool: {
            name: 'excel_create_pivot_table',
            description: 'Create a PivotTable from a data range',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet containing the source data',
                    },
                    range: {
                        type: 'string',
                        description: 'The source data range (e.g. "A1:D100")',
                    },
                    destination_sheet: {
                        type: 'string',
                        description: 'Sheet to place the PivotTable on. A new sheet is created if this is omitted.',
                    },
                    name: {
                        type: 'string',
                        description: 'Name for the PivotTable',
                    },
                },
                required: ['sheet_name', 'range', 'name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const rangeAddress = args.range;
            const destSheetName = args.destination_sheet;
            const name = args.name;
            const result = await Excel.run(async (context) => {
                const sourceSheet = await requireWorksheet(context, sheetName);
                const sourceRange = sourceSheet.getRange(rangeAddress);
                const destSheet = destSheetName
                    ? await requireWorksheet(context, destSheetName)
                    : context.workbook.worksheets.add();
                destSheet.load('name');
                const pivotTable = context.workbook.pivotTables.add(name, sourceRange, destSheet.getRange('A1'));
                pivotTable.load('name');
                await context.sync();
                return { pivotName: pivotTable.name, sheetName: destSheet.name };
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Created PivotTable "${result.pivotName}" on sheet "${result.sheetName}" from ${rangeAddress}`,
                    },
                ],
            };
        },
    },
    // ==================== NAMED RANGES ====================
    {
        tool: {
            name: 'excel_add_named_range',
            description: 'Create a named range in the workbook',
            inputSchema: {
                type: 'object',
                properties: {
                    name: {
                        type: 'string',
                        description: 'The name for the named range (e.g. "SalesData")',
                    },
                    range: {
                        type: 'string',
                        description: 'The range reference (e.g. "Sheet1!A1:D100")',
                    },
                    comment: {
                        type: 'string',
                        description: 'Optional comment for the named range',
                    },
                    sheet_scope: {
                        type: 'string',
                        description: 'If provided, the named range is scoped to this worksheet. Otherwise it is workbook-scoped.',
                    },
                },
                required: ['name', 'range'],
            },
        },
        execute: async (args) => {
            const name = args.name;
            const range = args.range;
            const comment = args.comment;
            const sheetScope = args.sheet_scope;
            await Excel.run(async (context) => {
                if (sheetScope) {
                    const worksheet = await requireWorksheet(context, sheetScope);
                    worksheet.names.add(name, range, comment);
                }
                else {
                    context.workbook.names.add(name, range, comment);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Created named range "${name}" pointing to ${range}${sheetScope ? ` (scoped to "${sheetScope}")` : ''}`,
                    },
                ],
            };
        },
    },
    // ==================== HYPERLINKS ====================
    {
        tool: {
            name: 'excel_set_hyperlink',
            description: 'Set a hyperlink on a cell',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    address: {
                        type: 'string',
                        description: 'The cell address (e.g. "A1")',
                    },
                    url: {
                        type: 'string',
                        description: 'The URL or cell reference (e.g. "https://example.com" or "Sheet2!A1")',
                    },
                    display_text: {
                        type: 'string',
                        description: 'The display text for the hyperlink',
                    },
                    tooltip: {
                        type: 'string',
                        description: 'The tooltip text',
                    },
                },
                required: ['sheet_name', 'address', 'url'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const address = args.address;
            const url = args.url;
            const displayText = args.display_text;
            const tooltip = args.tooltip;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(address);
                const isExternal = /^(https?:|mailto:|\/)/i.test(url);
                range.hyperlink = {
                    address: isExternal ? url : '',
                    documentReference: isExternal ? '' : url,
                    textToDisplay: displayText ?? url,
                    screenTip: tooltip ?? '',
                };
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Set hyperlink on ${address} on "${sheetName}" to "${url}"`,
                    },
                ],
            };
        },
    },
    // ==================== COMMENTS ====================
    {
        tool: {
            name: 'excel_add_comment',
            description: 'Add a threaded comment to a cell',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    address: {
                        type: 'string',
                        description: 'The cell address (e.g. "A1")',
                    },
                    text: {
                        type: 'string',
                        description: 'The comment text',
                    },
                },
                required: ['sheet_name', 'address', 'text'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const address = args.address;
            const text = args.text;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                worksheet.comments.add(worksheet.getRange(address), text);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Added comment to ${address} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== SHAPES & IMAGES ====================
    {
        tool: {
            name: 'excel_add_shape',
            description: 'Add a geometric shape or text box to a worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    type: {
                        type: 'string',
                        enum: [
                            'textBox',
                            'rectangle',
                            'roundedRectangle',
                            'ellipse',
                            'diamond',
                            'triangle',
                            'rightArrow',
                            'leftArrow',
                            'star5',
                        ],
                        description: 'The shape type',
                    },
                    text: {
                        type: 'string',
                        description: 'Text to place inside the shape',
                    },
                    left: {
                        type: 'number',
                        description: 'Left position in points (default: 100)',
                    },
                    top: {
                        type: 'number',
                        description: 'Top position in points (default: 100)',
                    },
                    width: {
                        type: 'number',
                        description: 'Width in points (default: 200)',
                    },
                    height: {
                        type: 'number',
                        description: 'Height in points (default: 100)',
                    },
                },
                required: ['sheet_name', 'type'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const shapeType = args.type;
            const text = args.text;
            const left = args.left ?? 100;
            const top = args.top ?? 100;
            const width = args.width ?? 200;
            const height = args.height ?? 100;
            const shapeName = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                let shape;
                if (shapeType === 'textBox') {
                    shape = worksheet.shapes.addTextBox(text ?? '');
                }
                else {
                    const geometricTypes = {
                        rectangle: Excel.GeometricShapeType.rectangle,
                        roundedRectangle: Excel.GeometricShapeType.roundRectangle,
                        ellipse: Excel.GeometricShapeType.ellipse,
                        diamond: Excel.GeometricShapeType.diamond,
                        triangle: Excel.GeometricShapeType.triangle,
                        rightArrow: Excel.GeometricShapeType.rightArrow,
                        leftArrow: Excel.GeometricShapeType.leftArrow,
                        star5: Excel.GeometricShapeType.star5,
                    };
                    shape = worksheet.shapes.addGeometricShape(geometricTypes[shapeType] ?? Excel.GeometricShapeType.rectangle);
                    if (text) {
                        shape.textFrame.textRange.text = text;
                    }
                }
                shape.left = left;
                shape.top = top;
                shape.width = width;
                shape.height = height;
                shape.load('name');
                await context.sync();
                return shape.name;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Added ${shapeType} shape "${shapeName}" on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_add_image',
            description: 'Insert a Base64-encoded image into a worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    base64: {
                        type: 'string',
                        description: 'The Base64-encoded image data (without data URI prefix)',
                    },
                    left: {
                        type: 'number',
                        description: 'Left position in points (default: 0)',
                    },
                    top: {
                        type: 'number',
                        description: 'Top position in points (default: 0)',
                    },
                },
                required: ['sheet_name', 'base64'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const base64 = args.base64;
            const left = args.left ?? 0;
            const top = args.top ?? 0;
            const shapeName = await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const image = worksheet.shapes.addImage(base64);
                image.left = left;
                image.top = top;
                image.load('name');
                await context.sync();
                return image.name;
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Inserted image "${shapeName}" on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== PROTECTION ====================
    {
        tool: {
            name: 'excel_protect_worksheet',
            description: 'Protect or unprotect a worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    protect: {
                        type: 'boolean',
                        description: 'true to protect, false to unprotect',
                    },
                    password: {
                        type: 'string',
                        description: 'Optional password for protection',
                    },
                    options: {
                        type: 'object',
                        description: 'Protection options: {allowFormatCells, allowFormatColumns, allowFormatRows, allowInsertColumns, allowInsertRows, allowInsertHyperlinks, allowDeleteColumns, allowDeleteRows, allowSort, allowAutoFilter, allowPivotTables, allowEditObjects, allowEditScenarios}. All are boolean.',
                    },
                },
                required: ['sheet_name', 'protect'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const protect = args.protect;
            const password = args.password;
            const options = args.options;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                if (protect) {
                    const protectionOptions = {};
                    if (options) {
                        if (options.allowFormatCells !== undefined)
                            protectionOptions.allowFormatCells = options.allowFormatCells;
                        if (options.allowFormatColumns !== undefined)
                            protectionOptions.allowFormatColumns = options.allowFormatColumns;
                        if (options.allowFormatRows !== undefined)
                            protectionOptions.allowFormatRows = options.allowFormatRows;
                        if (options.allowInsertColumns !== undefined)
                            protectionOptions.allowInsertColumns = options.allowInsertColumns;
                        if (options.allowInsertRows !== undefined)
                            protectionOptions.allowInsertRows = options.allowInsertRows;
                        if (options.allowInsertHyperlinks !== undefined)
                            protectionOptions.allowInsertHyperlinks = options.allowInsertHyperlinks;
                        if (options.allowDeleteColumns !== undefined)
                            protectionOptions.allowDeleteColumns = options.allowDeleteColumns;
                        if (options.allowDeleteRows !== undefined)
                            protectionOptions.allowDeleteRows = options.allowDeleteRows;
                        if (options.allowSort !== undefined)
                            protectionOptions.allowSort = options.allowSort;
                        if (options.allowAutoFilter !== undefined)
                            protectionOptions.allowAutoFilter = options.allowAutoFilter;
                        if (options.allowPivotTables !== undefined)
                            protectionOptions.allowPivotTables = options.allowPivotTables;
                        if (options.allowEditObjects !== undefined)
                            protectionOptions.allowEditObjects = options.allowEditObjects;
                        if (options.allowEditScenarios !== undefined)
                            protectionOptions.allowEditScenarios = options.allowEditScenarios;
                    }
                    worksheet.protection.protect(protectionOptions, password);
                }
                else {
                    worksheet.protection.unprotect(password);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `${protect ? 'Protected' : 'Unprotected'} worksheet "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== FREEZE PANES ====================
    {
        tool: {
            name: 'excel_freeze_panes',
            description: 'Freeze rows, columns, or a specific cell to create freeze panes',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    type: {
                        type: 'string',
                        enum: ['cell', 'rows', 'columns', 'unfreeze'],
                        description: 'Freeze type: "cell" freezes at a specific cell, "rows" freezes top N rows, "columns" freezes left N columns, "unfreeze" removes all freezes',
                    },
                    cell: {
                        type: 'string',
                        description: 'Cell address for "cell" freeze type (e.g. "B3" freezes row 1-2 and column A)',
                    },
                    count: {
                        type: 'number',
                        description: 'Number of rows or columns to freeze (for "rows" or "columns" type)',
                    },
                },
                required: ['sheet_name', 'type'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const freezeType = args.type;
            const cell = args.cell;
            const count = args.count ?? 1;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const freezePanes = worksheet.freezePanes;
                switch (freezeType) {
                    case 'cell':
                        if (!cell) {
                            throw new Error('The "cell" parameter is required when type is "cell" (e.g. "B3")');
                        }
                        freezePanes.freezeAt(worksheet.getRange(cell));
                        break;
                    case 'rows':
                        freezePanes.freezeRows(count);
                        break;
                    case 'columns':
                        freezePanes.freezeColumns(count);
                        break;
                    case 'unfreeze':
                        freezePanes.unfreeze();
                        break;
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: freezeType === 'unfreeze'
                            ? `Removed freeze panes on "${sheetName}"`
                            : `Froze ${freezeType === 'cell' ? `at ${cell}` : `${count} ${freezeType}`} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== COPY RANGE ====================
    {
        tool: {
            name: 'excel_copy_range',
            description: 'Copy data from one range to another, optionally across sheets',
            inputSchema: {
                type: 'object',
                properties: {
                    source_sheet: {
                        type: 'string',
                        description: 'The name of the source worksheet',
                    },
                    source_range: {
                        type: 'string',
                        description: 'The source range address (e.g. "A1:D10")',
                    },
                    dest_sheet: {
                        type: 'string',
                        description: 'The destination worksheet (default: same as source)',
                    },
                    dest_address: {
                        type: 'string',
                        description: 'The top-left cell of the destination (e.g. "A1")',
                    },
                    type: {
                        type: 'string',
                        enum: ['all', 'formulas', 'values', 'formats'],
                        description: 'What to copy (default: "all")',
                    },
                },
                required: ['source_sheet', 'source_range', 'dest_address'],
            },
        },
        execute: async (args) => {
            const sourceSheetName = args.source_sheet;
            const sourceRangeAddress = args.source_range;
            const destSheetName = args.dest_sheet ?? sourceSheetName;
            const destAddress = args.dest_address;
            const copyType = args.type ?? 'all';
            await Excel.run(async (context) => {
                const sourceSheet = await requireWorksheet(context, sourceSheetName);
                const sourceRange = sourceSheet.getRange(sourceRangeAddress);
                const destSheet = await requireWorksheet(context, destSheetName);
                const destRange = destSheet.getRange(destAddress);
                const rangeCopyType = copyType === 'formulas'
                    ? Excel.RangeCopyType.formulas
                    : copyType === 'values'
                        ? Excel.RangeCopyType.values
                        : copyType === 'formats'
                            ? Excel.RangeCopyType.formats
                            : Excel.RangeCopyType.all;
                destRange.copyFrom(sourceRange, rangeCopyType);
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Copied ${copyType} from ${sourceSheetName}!${sourceRangeAddress} to ${destSheetName}!${destAddress}`,
                    },
                ],
            };
        },
    },
    // ==================== PAGE LAYOUT / PRINTING ====================
    {
        tool: {
            name: 'excel_set_page_layout',
            description: 'Configure page layout and print settings for a worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    orientation: {
                        type: 'string',
                        enum: ['portrait', 'landscape'],
                        description: 'Page orientation',
                    },
                    paper_size: {
                        type: 'string',
                        enum: ['letter', 'legal', 'a4', 'a3', 'tabloid'],
                        description: 'Paper size',
                    },
                    print_area: {
                        type: 'string',
                        description: 'Print area range (e.g. "A1:H50"). Set to empty string to clear.',
                    },
                    print_title_rows: {
                        type: 'string',
                        description: 'Rows to repeat at top (e.g. "$1:$2")',
                    },
                    print_title_columns: {
                        type: 'string',
                        description: 'Columns to repeat at left (e.g. "$A:$B")',
                    },
                    print_gridlines: {
                        type: 'boolean',
                        description: 'Print gridlines',
                    },
                    print_headings: {
                        type: 'boolean',
                        description: 'Print row/column headings',
                    },
                    center_horizontally: {
                        type: 'boolean',
                        description: 'Center content horizontally on page',
                    },
                    center_vertically: {
                        type: 'boolean',
                        description: 'Center content vertically on page',
                    },
                    fit_to_pages_wide: {
                        type: 'number',
                        description: 'Fit to N pages wide (0 = auto)',
                    },
                    fit_to_pages_tall: {
                        type: 'number',
                        description: 'Fit to N pages tall (0 = auto)',
                    },
                    margins: {
                        type: 'object',
                        description: 'Page margins in inches: {top, bottom, left, right, header, footer}',
                    },
                },
                required: ['sheet_name'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const layout = worksheet.pageLayout;
                if (args.orientation !== undefined) {
                    layout.orientation =
                        args.orientation === 'landscape'
                            ? Excel.PageOrientation.landscape
                            : Excel.PageOrientation.portrait;
                }
                if (args.paper_size !== undefined) {
                    const sizes = {
                        letter: Excel.PaperType.letter,
                        legal: Excel.PaperType.legal,
                        a4: Excel.PaperType.a4,
                        a3: Excel.PaperType.a3,
                        tabloid: Excel.PaperType.tabloid,
                    };
                    layout.paperSize = sizes[args.paper_size] ?? Excel.PaperType.letter;
                }
                if (args.print_area !== undefined) {
                    const printArea = args.print_area;
                    if (printArea) {
                        layout.setPrintArea(printArea);
                    }
                    else {
                        layout.setPrintArea('');
                    }
                }
                if (args.print_title_rows !== undefined) {
                    layout.setPrintTitleRows(args.print_title_rows);
                }
                if (args.print_title_columns !== undefined) {
                    layout.setPrintTitleColumns(args.print_title_columns);
                }
                if (args.print_gridlines !== undefined) {
                    layout.printGridlines = args.print_gridlines;
                }
                if (args.print_headings !== undefined) {
                    layout.printHeadings = args.print_headings;
                }
                if (args.center_horizontally !== undefined) {
                    layout.centerHorizontally = args.center_horizontally;
                }
                if (args.center_vertically !== undefined) {
                    layout.centerVertically = args.center_vertically;
                }
                if (args.fit_to_pages_wide !== undefined || args.fit_to_pages_tall !== undefined) {
                    layout.zoom = {
                        horizontalFitToPages: args.fit_to_pages_wide ?? 0,
                        verticalFitToPages: args.fit_to_pages_tall ?? 0,
                    };
                }
                if (args.margins !== undefined) {
                    const margins = args.margins;
                    if (margins.top !== undefined)
                        layout.topMargin = margins.top * 72;
                    if (margins.bottom !== undefined)
                        layout.bottomMargin = margins.bottom * 72;
                    if (margins.left !== undefined)
                        layout.leftMargin = margins.left * 72;
                    if (margins.right !== undefined)
                        layout.rightMargin = margins.right * 72;
                    if (margins.header !== undefined)
                        layout.headerMargin = margins.header * 72;
                    if (margins.footer !== undefined)
                        layout.footerMargin = margins.footer * 72;
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Updated page layout for "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== GROUPING / OUTLINES ====================
    {
        tool: {
            name: 'excel_group_rows',
            description: 'Group or ungroup rows to create collapsible outlines',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    start_row: {
                        type: 'number',
                        description: 'The 1-based start row number',
                    },
                    end_row: {
                        type: 'number',
                        description: 'The 1-based end row number',
                    },
                    group: {
                        type: 'boolean',
                        description: 'true to group, false to ungroup (default: true)',
                    },
                },
                required: ['sheet_name', 'start_row', 'end_row'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const startRow = args.start_row;
            const endRow = args.end_row;
            const group = args.group ?? true;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(`${startRow}:${endRow}`);
                if (group) {
                    range.group(Excel.GroupOption.byRows);
                }
                else {
                    range.ungroup(Excel.GroupOption.byRows);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `${group ? 'Grouped' : 'Ungrouped'} rows ${startRow}-${endRow} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    {
        tool: {
            name: 'excel_group_columns',
            description: 'Group or ungroup columns to create collapsible outlines',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of the worksheet',
                    },
                    start_column: {
                        type: 'string',
                        description: 'The start column letter (e.g. "B")',
                    },
                    end_column: {
                        type: 'string',
                        description: 'The end column letter (e.g. "D")',
                    },
                    group: {
                        type: 'boolean',
                        description: 'true to group, false to ungroup (default: true)',
                    },
                },
                required: ['sheet_name', 'start_column', 'end_column'],
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const startCol = args.start_column;
            const endCol = args.end_column;
            const group = args.group ?? true;
            await Excel.run(async (context) => {
                const worksheet = await requireWorksheet(context, sheetName);
                const range = worksheet.getRange(`${startCol}:${endCol}`);
                if (group) {
                    range.group(Excel.GroupOption.byColumns);
                }
                else {
                    range.ungroup(Excel.GroupOption.byColumns);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `${group ? 'Grouped' : 'Ungrouped'} columns ${startCol}-${endCol} on "${sheetName}"`,
                    },
                ],
            };
        },
    },
    // ==================== WORKBOOK PROPERTIES ====================
    {
        tool: {
            name: 'excel_set_workbook_properties',
            description: 'Set workbook document properties (title, author, subject, etc.)',
            inputSchema: {
                type: 'object',
                properties: {
                    title: { type: 'string', description: 'Document title' },
                    author: { type: 'string', description: 'Document author' },
                    subject: { type: 'string', description: 'Document subject' },
                    keywords: { type: 'string', description: 'Document keywords' },
                    comments: { type: 'string', description: 'Document comments' },
                    category: { type: 'string', description: 'Document category' },
                    manager: { type: 'string', description: 'Manager name' },
                    company: { type: 'string', description: 'Company name' },
                },
            },
        },
        execute: async (args) => {
            await Excel.run(async (context) => {
                const props = context.workbook.properties;
                if (args.title !== undefined)
                    props.title = args.title;
                if (args.author !== undefined)
                    props.author = args.author;
                if (args.subject !== undefined)
                    props.subject = args.subject;
                if (args.keywords !== undefined)
                    props.keywords = args.keywords;
                if (args.comments !== undefined)
                    props.comments = args.comments;
                if (args.category !== undefined)
                    props.category = args.category;
                if (args.manager !== undefined)
                    props.manager = args.manager;
                if (args.company !== undefined)
                    props.company = args.company;
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: `Updated workbook properties`,
                    },
                ],
            };
        },
    },
    // ==================== CALCULATE ====================
    {
        tool: {
            name: 'excel_calculate',
            description: 'Force recalculation of the workbook or a specific worksheet',
            inputSchema: {
                type: 'object',
                properties: {
                    sheet_name: {
                        type: 'string',
                        description: 'The name of a specific worksheet to recalculate. If omitted, recalculates the entire workbook.',
                    },
                    type: {
                        type: 'string',
                        enum: ['recalculate', 'full', 'fullRebuild'],
                        description: 'Calculation type for a whole-workbook recalculation (default: "recalculate"). Ignored when sheet_name is given.',
                    },
                },
            },
        },
        execute: async (args) => {
            const sheetName = args.sheet_name;
            const calcType = args.type ?? 'recalculate';
            const calcTypes = {
                recalculate: Excel.CalculationType.recalculate,
                full: Excel.CalculationType.full,
                fullRebuild: Excel.CalculationType.fullRebuild,
            };
            const officeCalcType = Object.hasOwn(calcTypes, calcType)
                ? calcTypes[calcType]
                : undefined;
            // `type` only applies to a whole-workbook recalculation; the sheet path ignores it.
            if (!sheetName && !officeCalcType) {
                throw new Error(`Invalid calculation type "${calcType}". Use one of: recalculate, full, fullRebuild`);
            }
            await Excel.run(async (context) => {
                if (sheetName) {
                    const worksheet = await requireWorksheet(context, sheetName);
                    worksheet.calculate(true);
                }
                else {
                    context.workbook.application.calculate(officeCalcType);
                }
                await context.sync();
            });
            return {
                content: [
                    {
                        type: 'text',
                        text: sheetName
                            ? `Recalculated worksheet "${sheetName}"`
                            : `Recalculated workbook (${calcType})`,
                    },
                ],
            };
        },
    },
];
